import React, { FormEvent, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { DataSnapshot, ref, update } from '@firebase/database';
import { database } from '../api/firebase';
import { IVideo } from '../types/room';
import { onValue } from 'firebase/database';
import useRoomcode from '../hooks/useRoomcode';

// if difference (in seconds) between client vs server video seconds is above this, then seek
const SEEK_THRESHOLD = 7;
const CLEAR_CMD = '/clear';

interface VideoProgressState {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
}

interface VideoPlayerProps { }

const VideoPlayer: React.FC<VideoPlayerProps> = () => {
  const roomcode = useRoomcode();
  const videoRef = useRef<ReactPlayer>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const [video, setVideo] = useState<IVideo>({ url: '', isPlaying: false, elapsed: 0 });
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const videoRef = ref(database, `bubbles/${roomcode}/video`);
    const unsubscribe = onValue(videoRef, (snapshot: DataSnapshot) => {
      setVideo(snapshot.val());
    });
    return unsubscribe;
  }, [roomcode]);

  useEffect(() => {
    function shouldSeek(): boolean {
      if (!videoRef.current) return false;
      return Math.abs(video.elapsed - videoRef.current.getCurrentTime()) > SEEK_THRESHOLD;
    }
    if (shouldSeek()) {
      videoRef.current?.seekTo(video.elapsed, 'seconds');
    }
  }, [video.elapsed]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const url = getSanitizedUrl();
    if (!url) return;
    if (url === CLEAR_CMD) return handlePlayableUrl('');
    ReactPlayer.canPlay(url) ? handlePlayableUrl(url) : handleUnplayableUrl();
  };

  const getSanitizedUrl = (): string => {
    const url: string = urlInputRef!.current!.value;
    return url.trim();
  }

  const clearUrlInput = () => {
    urlInputRef!.current!.value = '';
  }

  const handlePlayableUrl = (url: string) => {
    updateDbPathWithValue(getVideoChildDbPath('url'), url);
    setError('');
    clearUrlInput();
  }

  const handleUnplayableUrl = () => {
    setError('this url cannot be played');
    clearUrlInput();
  }

  const handlePlay = () => {
    updateDbPathWithValue(getVideoChildDbPath('isPlaying'), true);
  }

  const handlePause = () => {
    updateDbPathWithValue(getVideoChildDbPath('isPlaying'), false);
  }

  const handleProgress = (state: VideoProgressState) => {
    updateDbPathWithValue(getVideoChildDbPath('elapsed'), state.playedSeconds);
  }

  const handleEnded = () => {
    // updateDbPathWithValue(getVideoChildDbPath('url'), '');
  }

  const handleSeek = (event: any) => {
    // set db to pause
    // set db to seconds elapsed
    // set db to unpause
    console.log('seek to: ' + event);
  }

  const handleError = (error: any, data: any) => {
    console.log(error);
    console.log(data);
    setError('something just blew up :/');
    updateDbPathWithValue(getVideoChildDbPath('url'), '');
  }

  const getVideoChildDbPath = (child: string) => {
    return `bubbles/${roomcode}/video/${child}`;
  }

  const updateDbPathWithValue = (path: string, value: any) => {
    const updates: any = {};
    updates[path] = value;
    update(ref(database), updates);
  }

  return (
    <div className='bg-black bg-opacity-50 flex flex-col justify-center items-center h-1/2 overflow-x-hidden sm:h-full'>
      <p className={'font-mono ' + (video.url ? 'hidden' : '')}>
        {error !== '' ? error : 'enter a video to get started'}
      </p>
      <form onSubmit={handleSubmit} className={video.url ? 'w-full' : ''}>
        <input
          className={
            'text-white bg-black bg-opacity-30 border-none p-1 outline-none mt-1 ' +
            (video.url ? 'w-full' : 'text-center')
          }
          ref={urlInputRef}
          type='text'
          placeholder='youtube, soundcloud, etc'
        />
      </form>
      <ReactPlayer
        className={!video.url ? 'hidden' : ''}
        controls
        url={video.url}
        playing={video.isPlaying}
        ref={videoRef}
        width='100%'
        height='100%'
        onPlay={handlePlay}
        onPause={handlePause}
        onProgress={handleProgress}
        onEnded={handleEnded}
        onSeek={handleSeek}
        onError={handleError}
      />
    </div >
  );
}

export default VideoPlayer;
